import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Title,
  Box,
  Flex,
  TableProps,
  Modal,
  Button,
  Menu,
  Tooltip,
  Text,
  Loader,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  MantineReactTable,
  MRT_ColumnDef,
  MRT_PaginationState,
  MRT_TableOptions,
  MRT_Row,
  useMantineReactTable,
} from 'mantine-react-table';
import { format } from 'date-fns';
import { TFunction } from 'i18next';
import { useQueryParams, StringParam } from 'use-query-params';
import { ManifestApiResponse } from '@manifest-cyber/types/interface/apiResponse';
import { GetAiModelsQueryResponseData } from '@manifest-cyber/types/interface/contracts/models';
import { getDefaultTableOptions } from '../../components/MRT/ManifestMRT';
import BasicSearch from '../../components/BasicSearch';
import DataTableFooter from '../../components/DataTables/DataTableFooter';
import { RiskScoreColumn } from './columns/RiskScoreColumn';
import ClickableRegion from '../../components/ClickableRegion';
import styles from './InventoryList.module.scss';
import { AxiosProxy } from '../../api/axiosProxy/axiosProxy';
import { TextComponent } from '@manifest-cyber/design-system/lib/components/TextComponent';
import { useTableSorting } from '../../hooks/utils/useTableSorting';
import { Helmet } from 'react-helmet-async';
import { ManifestTabs } from '../../components/ManifestTabs/ManifestTabs';
import ManifestBadge from '../../components/ManifestBadge/ManifestBadge';
import Icon from '../../components/Icon';
import { useNotifications } from '../../hooks/utils/useNotifications';
import { captureExceptionWithMessage } from '../../lib/sentry/captureExceptionWithMessage/captureExceptionWithMessage';
import appConfig from '../../configs/appConfig';
import { useFetchUserHasPermissions } from '../../api/user/useFetchUserHasPermissions';

enum INVENTORY_TABS {
  APPROVED_MODELS = 'approvedModels',
  MODEL_REQUESTS = 'modelRequests',
}

// Version cell component with max-width and tooltip
const VersionCell = ({ value }: { value: string | null }) => {
  const version = value || '';
  return (
    <Tooltip disabled={(version?.length ?? 0) <= 20} label={version}>
      <Box sx={{ maxWidth: '150px', overflow: 'hidden' }}>
        <Text
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {version}
        </Text>
      </Box>
    </Tooltip>
  );
};

// Supplier cell component with max-width and tooltip
const SupplierCell = ({ value }: { value: string | null }) => {
  const supplier = value || '';
  return (
    <Tooltip disabled={(supplier?.length ?? 0) <= 25} label={supplier}>
      <Box sx={{ maxWidth: '150px', overflow: 'hidden' }}>
        <Text
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {supplier}
        </Text>
      </Box>
    </Tooltip>
  );
};

// License cell component with max-width and tooltip
const LicenseCell = ({ value }: { value: string | null }) => {
  const license = value || '';
  return (
    <Tooltip disabled={(license?.length ?? 0) <= 25} label={license}>
      <Box sx={{ maxWidth: '150px', overflow: 'hidden' }}>
        <Text
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            width: '100%',
          }}
        >
          {license}
        </Text>
      </Box>
    </Tooltip>
  );
};

// URL cell component with max-width and tooltip
const UrlCell = ({ value, t }: { value: string | null; t: any }) => {
  const url = value || '';
  return url ? (
    <Tooltip label={url}>
      <Box sx={{ maxWidth: '150px', overflow: 'hidden' }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            display: 'block',
            width: '100%',
          }}
        >
          {url}
        </a>
      </Box>
    </Tooltip>
  ) : (
    <span className="table-na">{t('global.unknown')}</span>
  );
};

// Hook for approved models
const useApprovedModels = (
  searchQuery: string,
  page: number,
  pageSize: number,
  sortColumn?: string,
  sortType?: -1 | 1,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['ai-models-approved', searchQuery, page, pageSize, sortColumn, sortType],
    queryFn: async (): Promise<ManifestApiResponse<GetAiModelsQueryResponseData[]>> => {
      const params = new URLSearchParams({
        search: searchQuery || '',
        page: page.toString(),
        pageSize: pageSize.toString(),
      });

      if (sortColumn) {
        params.append('sortBy', sortColumn);
      }
      if (sortType !== undefined) {
        const sortOrder = sortType === -1 ? 'desc' : 'asc';
        params.append('sortOrder', sortOrder);
      }

      const response = await AxiosProxy.get({
        url: `models/inventory?${params.toString()}`,
      });
      return response;
    },
    enabled,
  });
};

// Hook for model requests
const useModelRequests = (
  searchQuery: string,
  page: number,
  pageSize: number,
  sortColumn?: string,
  sortType?: -1 | 1,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: ['ai-models-requests', searchQuery, page, pageSize, sortColumn, sortType],
    queryFn: async (): Promise<ManifestApiResponse<GetAiModelsQueryResponseData[]>> => {
      const params = new URLSearchParams({
        search: searchQuery || '',
        page: page.toString(),
        pageSize: pageSize.toString(),
        showRequestsOnly: 'pending',
      });

      if (sortColumn) {
        params.append('sortBy', sortColumn);
      }
      if (sortType !== undefined) {
        const sortOrder = sortType === -1 ? 'desc' : 'asc';
        params.append('sortOrder', sortOrder);
      }

      const response = await AxiosProxy.get({
        url: `models/inventory?${params.toString()}`,
      });
      return response;
    },
    enabled,
  });
};

// Define columns for approved models
const getApprovedModelsColumns = (t: TFunction): MRT_ColumnDef<GetAiModelsQueryResponseData>[] => [
  {
    accessorKey: 'name',
    header: t('mosaic.inventoryList.columns.modelName'),
    Cell: ({ cell, row }) => {
      return (
        <ClickableRegion
          href={`/ai-explorer/model/${row.original._id}`}
          className={styles.modelName}
        >
          {cell.getValue() as string}
        </ClickableRegion>
      );
    },
  },
  {
    accessorKey: 'version',
    header: t('mosaic.inventoryList.columns.version'),
    enableSorting: false, // Backend doesn't support sorting by version
    size: 150,
    Cell: ({ cell }) => {
      return <VersionCell value={cell.getValue() as string} />;
    },
  }, {
    accessorKey: 'riskScore',
    header: t('mosaic.inventoryList.columns.risk'),
    // Map to backend field name 'risk' for sorting
    id: 'risk',
    Cell: ({ cell, row }) => {
      return <RiskScoreColumn row={row} cell={cell} />;
    },
  },
  {
    accessorKey: 'supplier',
    header: t('mosaic.inventoryList.columns.supplier'),
    size: 200,
    Cell: ({ cell }) => {
      return <SupplierCell value={cell.getValue() as string} />;
    },
  },
  {
    accessorKey: 'license',
    header: t('mosaic.inventoryList.columns.license'),
    size: 200,
    Cell: ({ cell }) => {
      return <LicenseCell value={cell.getValue() as string} />;
    },
  },
  {
    accessorKey: 'addedBy',
    header: t('mosaic.inventoryList.columns.addedBy'),
    enableSorting: false, // Backend doesn't support sorting by addedBy
  },
  {
    accessorKey: 'url',
    header: t('mosaic.inventoryList.columns.url'),
    size: 250,
    Cell: ({ cell }) => {
      return <UrlCell value={cell.getValue() as string} t={t} />;
    },
  },
  {
    accessorKey: 'dateCreated',
    header: t('mosaic.inventoryList.columns.dateUploaded'),
    Cell: ({ cell }) => {
      const date = cell.getValue() as Date;
      return date
        ? format(new Date(date), 'MMM dd, yyyy, HH:mm') + ' UTC'
        : t('global.unknown');
    },
  },
];

// Define columns for model requests
const getModelRequestsColumns = (
  t: TFunction,
  onApprove: (model: GetAiModelsQueryResponseData) => void,
  onDeny: (model: GetAiModelsQueryResponseData) => void,
  onDelete: (model: GetAiModelsQueryResponseData) => void,
  loadingModelId: string | null,
): MRT_ColumnDef<GetAiModelsQueryResponseData>[] => [
    {
      accessorKey: 'name',
      header: t('mosaic.inventoryList.columns.modelName'),
      Cell: ({ cell, row }) => {
        return (
          <ClickableRegion
            href={`/ai-explorer/model/${row.original._id}`}
            className={styles.modelName}
          >
            {cell.getValue() as string}
          </ClickableRegion>
        );
      },
    }, {
      accessorKey: 'version',
      header: t('mosaic.inventoryList.columns.version'),
      enableSorting: false, // Backend doesn't support sorting by version
      size: 150,
      Cell: ({ cell }) => {
        return <VersionCell value={cell.getValue() as string} />;
      },
    }, {
      accessorKey: 'riskScore',
      header: t('mosaic.inventoryList.columns.risk'),
      // Map to backend field name 'risk' for sorting
      id: 'risk',
      Cell: ({ cell, row }) => {
        return <RiskScoreColumn row={row} cell={cell} />;
      },
    },
    {
      accessorKey: 'supplier',
      header: t('mosaic.inventoryList.columns.supplier'),
      size: 200,
      Cell: ({ cell }) => {
        return <SupplierCell value={cell.getValue() as string} />;
      },
    },
    {
      accessorKey: 'license',
      header: t('mosaic.inventoryList.columns.license'),
      size: 200,
      Cell: ({ cell }) => {
        return <LicenseCell value={cell.getValue() as string} />;
      },
    },
    {
      accessorKey: 'addedBy',
      header: t('mosaic.inventoryList.columns.requestedBy'),
      enableSorting: false, // Backend doesn't support sorting by requestedByUser
    },
    {
      accessorKey: 'inventoryRequestedBy.dateCreated',
      header: t('mosaic.inventoryList.columns.requestedAt'),
      Cell: ({ cell }) => {
        const date = cell.getValue() as Date;
        return date
          ? format(new Date(date), 'MMM dd, yyyy, HH:mm') + ' UTC'
          : t('global.unknown');
      },
    }, {
      accessorKey: 'url',
      header: t('mosaic.inventoryList.columns.url'),
      size: 250,
      Cell: ({ cell }) => {
        return <UrlCell value={cell.getValue() as string} t={t} />;
      },
    }, {
      id: 'actions',
      header: t('mosaic.inventoryList.columns.actions'),
      enableSorting: false,
      size: 300, // Set a fixed width for the actions column
      minSize: 300, // Ensure minimum width
      Cell: ({ row }) => {
        const isLoading = loadingModelId === row.original._id;

        if (isLoading) {
          return (
            <Flex justify="center" align="center" h={32}>
              <Loader size="sm" />
            </Flex>
          );
        }

        return (
          <Flex gap="xs">
            <Button
              size="xs"
              variant="filled"
              color="green"
              onClick={() => onApprove(row.original)}
            >
              {t('mosaic.inventoryList.actions.approve')}
            </Button>
            <Button
              size="xs"
              variant="filled"
              color="red"
              onClick={() => onDeny(row.original)}
            >
              {t('mosaic.inventoryList.actions.deny')}
            </Button>
            <Button
              size="xs"
              variant="outline"
              color="gray"
              onClick={() => onDelete(row.original)}
            >
              {t('mosaic.inventoryList.actions.delete')}
            </Button>
          </Flex>
        );
      },
    },
  ];

const AIModelsPage: React.FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { success: showSuccessNotification, error: showErrorNotification } =
    useNotifications();
  const defaultTableOptions = getDefaultTableOptions<GetAiModelsQueryResponseData>();

  // Check if user can manage AI model requests
  const canManageModelRequests = useFetchUserHasPermissions({
    requiredPermissions: ['update:ai-model-request'],
  });

  const [activeTab, setActiveTab] = useState<INVENTORY_TABS>(
    INVENTORY_TABS.APPROVED_MODELS,
  );
  // Delete modal state
  const [deleteModalOpened, setDeleteModalOpened] = useState(false);
  const [modelToDelete, setModelToDelete] = useState<GetAiModelsQueryResponseData | null>(
    null,
  );

  // Loading state for model actions
  const [loadingModelId, setLoadingModelId] = useState<string | null>(null);

  // Delete mutation
  const { mutate: deleteModel, isLoading: isDeleting } = useMutation<
    unknown,
    unknown,
    string
  >(
    async (modelId: string) => {
      return AxiosProxy.delete({
        url: `model-analysis/inventory/${modelId}`,
      });
    },
    {
      onSuccess: () => {
        showSuccessNotification({
          title: t('mosaic.inventoryList.notification.modelDeletedSuccessfully'),
        });
        setDeleteModalOpened(false);
        setModelToDelete(null);
      },
      onError: (err) => {
        captureExceptionWithMessage('Failed to delete model from inventory', err);
        showErrorNotification({
          title: t('mosaic.inventoryList.notification.errorDeletingModel'),
        });
      }, onSettled: () => {
        // Invalidate both approved models and requests queries to keep data fresh
        queryClient.invalidateQueries({
          queryKey: ['ai-models-approved'],
        });
        queryClient.invalidateQueries({
          queryKey: ['ai-models-requests'],
        });
      },
    },
  );

  const handleDeleteClick = (model: GetAiModelsQueryResponseData) => {
    setModelToDelete(model);
    setDeleteModalOpened(true);
  };

  const handleDeleteConfirm = () => {
    if (modelToDelete) {
      deleteModel(modelToDelete._id);
    }
  };
  const handleDeleteCancel = () => {
    setDeleteModalOpened(false);
    setModelToDelete(null);
  };
  // Action handlers for model requests
  const handleApprove = async (model: GetAiModelsQueryResponseData) => {
    setLoadingModelId(model._id);
    try {
      await AxiosProxy.put({
        url: `model-analysis/inventory/${model._id}/request/approve`,
      });

      showSuccessNotification({
        title: t('mosaic.inventoryList.notification.modelApprovedSuccessfully'),
      });

      // Refresh both queries to update the data
      queryClient.invalidateQueries({ queryKey: ['ai-models-approved'] });
      queryClient.invalidateQueries({ queryKey: ['ai-models-requests'] });
    } catch (err) {
      captureExceptionWithMessage('Failed to approve model request', err);
      showErrorNotification({
        title: t('mosaic.inventoryList.notification.errorApprovingModel'),
      });
    } finally {
      setLoadingModelId(null);
    }
  };

  const handleDeny = async (model: GetAiModelsQueryResponseData) => {
    setLoadingModelId(model._id);
    try {
      await AxiosProxy.put({
        url: `model-analysis/inventory/${model._id}/request/deny`,
      });

      showSuccessNotification({
        title: t('mosaic.inventoryList.notification.modelDeniedSuccessfully'),
      });

      // Refresh requests query to update the data
      queryClient.invalidateQueries({ queryKey: ['ai-models-requests'] });
    } catch (err) {
      captureExceptionWithMessage('Failed to deny model request', err);
      showErrorNotification({
        title: t('mosaic.inventoryList.notification.errorDenyingModel'),
      });
    } finally {
      setLoadingModelId(null);
    }
  };

  const handleDeleteRequest = async (model: GetAiModelsQueryResponseData) => {
    setLoadingModelId(model._id);
    try {
      await AxiosProxy.delete({
        url: `model-analysis/inventory/${model._id}/request`,
      });

      showSuccessNotification({
        title: t('mosaic.inventoryList.notification.modelRequestDeletedSuccessfully'),
      });

      // Refresh requests query to update the data
      queryClient.invalidateQueries({ queryKey: ['ai-models-requests'] });
    } catch (err) {
      captureExceptionWithMessage('Failed to delete model request', err);
      showErrorNotification({
        title: t('mosaic.inventoryList.notification.errorDeletingModelRequest'),
      });
    } finally {
      setLoadingModelId(null);
    }
  };

  const handleDownloadRequest = async (modelId: string, type: 'bom' | 'report') => {
    try {
      const params = new URLSearchParams({
        modelId: modelId.toString(),
        downloadType: type,
      });
      if (type === 'report') params.append('downloadFormat', 'docx');

      window.open(
        `//${appConfig.apiUrl}/v${appConfig.apiVersion}/model-analysis/download?${params.toString()}`,
        '_blank',
      );
    } catch (e) {
      captureExceptionWithMessage(`InventoryList handleDownloadRequest:`, e);
      showErrorNotification({
        title: t('mosaic.modelDetail.notification.errorDownloading'),
      });
    }
  };

  // Initialize table sorting with clean hook
  const { sorting, setSorting, getSortParameters } = useTableSorting({
    tableId: 'ai-models',
    defaultSortColumn: 'dateCreated',
    defaultSortDirection: 'desc',
  });

  // Query params management - only include search in URL
  const [query, setQuery] = useQueryParams({
    search: StringParam,
  });

  const [searchQuery, setSearchQuery] = useState<string>(query.search || '');

  // Local pagination state - keep page out of URL
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0, // Default to first page
    pageSize: 10,
  });

  // Sync URL search param with state
  useEffect(() => {
    if (query.search !== searchQuery) {
      setQuery({ search: searchQuery || undefined });
    }
  }, [searchQuery, setQuery]);
  // Update search state when URL changes
  useEffect(() => {
    if (query.search !== searchQuery) {
      setSearchQuery(query.search || '');
    }
  }, [query.search]);

  // Ensure user with no permissions can't be on Model Requests tab
  useEffect(() => {
    if (activeTab === INVENTORY_TABS.MODEL_REQUESTS && !canManageModelRequests) {
      setActiveTab(INVENTORY_TABS.APPROVED_MODELS);
    }
  }, [activeTab, canManageModelRequests]);  // Handle tab changes - reset pagination and refresh data
  const handleTabChange = (newTab: INVENTORY_TABS) => {
    // If user tries to switch to Model Requests tab without permission, keep them on approved models
    if (newTab === INVENTORY_TABS.MODEL_REQUESTS && !canManageModelRequests) {
      return;
    }

    setActiveTab(newTab);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to first page

    // Refetch data for the new tab to ensure we have the latest data
    if (newTab === INVENTORY_TABS.MODEL_REQUESTS) {
      modelRequestsQuery.refetch();
    } else {
      approvedModelsQuery.refetch();
    }
  };
  // Get sort parameters for API call
  const sortParameters = getSortParameters();

  // Fetch data using separate hooks for each tab
  const approvedModelsQuery = useApprovedModels(
    searchQuery,
    pagination.pageIndex + 1,
    pagination.pageSize,
    sortParameters.sortColumn,
    sortParameters.sortType,
    activeTab === INVENTORY_TABS.APPROVED_MODELS, // Only fetch when tab is active
  ); const modelRequestsQuery = useModelRequests(
    searchQuery,
    pagination.pageIndex + 1,
    pagination.pageSize,
    sortParameters.sortColumn,
    sortParameters.sortType,
    canManageModelRequests, // Always fetch if user has permissions to get initial count
  );

  // Handle search input changes
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPagination((prev) => ({ ...prev, pageIndex: 0 })); // Reset to the first page
  };

  // Approved models table configuration
  const approvedModelsTableConfig: MRT_TableOptions<GetAiModelsQueryResponseData> = {
    ...defaultTableOptions,
    mantineTableProps: { 'data-testid': 'ai-models-table' } as TableProps,
    mantinePaperProps: {
      className: 'manifest-data-table-no-footer',
    },
    layoutMode: 'grid',
    columns: getApprovedModelsColumns(t),
    data: approvedModelsQuery.data?.data || [],
    enableFilters: false,
    enablePagination: false,
    enableRowActions: true,
    enableRowSelection: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: approvedModelsQuery.data?.queryInfo?.totalCount || 0,
    initialState: {
      ...defaultTableOptions.initialState,
      showGlobalFilter: true,
    },
    state: {
      globalFilter: searchQuery,
      pagination,
      sorting,
      isLoading: approvedModelsQuery.isLoading,
      showLoadingOverlay: approvedModelsQuery.isFetching,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderRowActionMenuItems: ({
      row,
    }: {
      row: MRT_Row<GetAiModelsQueryResponseData>;
    }) => {
      return (
        <>
          <Menu.Item
            icon={<Icon icon="arrow-down-to-bracket" />}
            onClick={() => handleDownloadRequest(row.original._id, 'bom')}
          >
            {t('mosaic.inventoryList.actions.downloadAiSbom')}
          </Menu.Item>
          <Menu.Item
            icon={<Icon icon="trash" />}
            onClick={() => handleDeleteClick(row.original)}
          >
            {t('mosaic.inventoryList.actions.removeModel')}
          </Menu.Item>
        </>
      );
    },
    renderTopToolbar: () => {
      return (
        <Flex justify="space-between" m="1rem 0">
          <Flex gap="xs">
            <Box miw="260px">
              <BasicSearch
                initialValue={searchQuery}
                handleSearch={handleSearchChange}
                placeholder={t('mosaic.inventoryList.search')}
              />
            </Box>
          </Flex>
        </Flex>
      );
    },
  };
  // Model requests table configuration
  const modelRequestsTableConfig: MRT_TableOptions<GetAiModelsQueryResponseData> = {
    ...defaultTableOptions,
    mantineTableProps: { 'data-testid': 'ai-models-requests-table' } as TableProps,
    mantinePaperProps: {
      className: 'manifest-data-table-no-footer',
    },
    layoutMode: 'semantic', // Use semantic layout to prevent action column cutoff
    columns: getModelRequestsColumns(t, handleApprove, handleDeny, handleDeleteRequest, loadingModelId),
    data: modelRequestsQuery.data?.data || [],
    enableFilters: false,
    enablePagination: false,
    enableRowActions: false, // No row actions for model requests
    enableRowSelection: false,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: modelRequestsQuery.data?.queryInfo?.totalCount || 0,
    initialState: {
      ...defaultTableOptions.initialState,
      showGlobalFilter: true,
    },
    state: {
      globalFilter: searchQuery,
      pagination,
      sorting,
      isLoading: modelRequestsQuery.isLoading,
      showLoadingOverlay: modelRequestsQuery.isFetching,
    },
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbar: () => {
      return (
        <Flex justify="space-between" m="1rem 0">
          <Flex gap="xs">
            <Box miw="260px">
              <BasicSearch
                initialValue={searchQuery}
                handleSearch={handleSearchChange}
                placeholder={t('mosaic.inventoryList.search')}
              />
            </Box>
          </Flex>
        </Flex>
      );
    },
  };

  const approvedModelsTable = useMantineReactTable<GetAiModelsQueryResponseData>(approvedModelsTableConfig);
  const modelRequestsTable = useMantineReactTable<GetAiModelsQueryResponseData>(modelRequestsTableConfig);

  return (
    <>
      <Helmet title={t('mosaic.inventoryList.page-title')}>
        <meta name="description" content={t('app.oneliner')} />
      </Helmet>
      <section className="page-ai-models anim-slideInUpShort">
        <Container size="xl" py="md">
          <Title order={1} mb="xs" className="anim-slideInDownShort">
            {t('mosaic.inventoryList.page-title')}
          </Title>
          <ManifestTabs
            value={activeTab}
            onTabChange={(newTab) => handleTabChange(newTab as INVENTORY_TABS)}
          >
            <ManifestTabs.List>
              <ManifestTabs.Tab value={INVENTORY_TABS.APPROVED_MODELS}>
                <Flex gap="xs">
                  {t('mosaic.inventoryList.tabs.approvedModels')}
                  {approvedModelsQuery.data?.queryInfo?.totalCount && approvedModelsQuery.data.queryInfo.totalCount > 0 && (
                    <ManifestBadge
                      children={approvedModelsQuery.data.queryInfo.totalCount}
                      active={activeTab === INVENTORY_TABS.APPROVED_MODELS}
                    />
                  )}
                </Flex>
              </ManifestTabs.Tab>
              {canManageModelRequests && (
                <ManifestTabs.Tab value={INVENTORY_TABS.MODEL_REQUESTS}>
                  <Flex gap="xs">
                    {t('mosaic.inventoryList.tabs.modelRequests')}
                    {
                      (modelRequestsQuery.data?.queryInfo?.totalCount &&
                        modelRequestsQuery.data?.queryInfo?.totalCount > 0) ?
                        (
                          <ManifestBadge
                            children={modelRequestsQuery.data.queryInfo.totalCount || 0}
                            active={activeTab === INVENTORY_TABS.MODEL_REQUESTS}
                          />
                        ) : null
                    }
                  </Flex>
                </ManifestTabs.Tab>
              )}
            </ManifestTabs.List>
          </ManifestTabs>

          {activeTab === INVENTORY_TABS.APPROVED_MODELS && (
            <div className="list-ai-models">
              <MantineReactTable table={approvedModelsTable} />
              <DataTableFooter
                currentPage={pagination.pageIndex}
                limitPerPage={pagination.pageSize}
                totalResults={approvedModelsQuery.data?.queryInfo?.totalCount || 0}
                onChangePage={setPagination}
              />
            </div>
          )}

          {activeTab === INVENTORY_TABS.MODEL_REQUESTS && canManageModelRequests && (
            <div className="list-ai-models">
              <MantineReactTable table={modelRequestsTable} />
              <DataTableFooter
                currentPage={pagination.pageIndex}
                limitPerPage={pagination.pageSize}
                totalResults={modelRequestsQuery.data?.queryInfo?.totalCount || 0}
                onChangePage={setPagination}
              />
            </div>
          )}

          {/* Removal confirmation modal */}
          <Modal
            opened={deleteModalOpened}
            onClose={() => setDeleteModalOpened(false)}
            title={
              <span className={styles.deleteModalTitle}>
                {t('mosaic.inventoryList.deleteModal.title')}
              </span>
            }
          >
            <TextComponent>
              {t('mosaic.inventoryList.deleteModal.description')}
            </TextComponent>
            <Flex justify="flex-end" mt="md">
              <Button variant="default" color="gray" onClick={handleDeleteCancel} mr="sm">
                {t('global.cancel')}
              </Button>
              <Button color="red" onClick={handleDeleteConfirm} loading={isDeleting}>
                {t('global.delete')}
              </Button>
            </Flex>
          </Modal>
        </Container>
      </section>
    </>
  );
};

export default AIModelsPage;
